/* istanbul ignore file */
import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import { updateInStorage } from '../../utils/get-location/location-from-storage';
import './OnBoarding.css';
import styles from './Onboarding.module.css';
import { getWorkflow, languageForApi } from '../../utils/network';
import QuestionCard from './QuestionCard';
import ChatBot from '../ChatBot/ChatBot';
import { questionId, nextQuestionId, questionById, setAnswer, formatAnswers, getComputeRisk, getUserId, responseForPosting, deleteQuestionIdsFromSessionStorage } from './utils';
import { parseQueryString, isDesktopScreen, logError, appStorage } from '../../utils/utils';
import useTranslation from '../../hooks/use-translation';
import useLoader from '../../hooks/use-loader';
import Error from '../Commons/error';
import { fireSurveyResultEventAtUrl } from '../../utils/events';

const inlineStyles = {
  desktopContainer: {
    questionnaire: {
      width: '35em',
      alignContent: 'center',
      marginLeft: 'auto',
      marginRight: 'auto',
    },
    chatbot: {
      width: '40em',
      alignContent: 'center',
      marginLeft: 'auto',
      marginRight: 'auto',
    }
  },
  mobileContainer: {
    questionnaire: {
      width: '100%'
    },
    chatbot: {
      display: 'flex',
      flex: '1',
      flexDirection: 'column',
      width: '100%'
    }
  }
};

export default () => {
  const [state, setState] = useState({
    allQuestions: [],
    questions: [],
    isLoading: false,
    workflow_id: null,
    actionName: 'NONE',
    renderedQuestions: [],
    workflowResponse: {}
  });
  const { t, language, hasChatBotQuestionnaire } = useTranslation();
  const { showLoading, hideLoading } = useLoader();
  const { questionId: currentQuestionId } = useParams();
  const history = useHistory();

  useEffect(() => {
    (async () => {
      showLoading();
      try {
        const { location = {} } = history;
        const { state: routeState = {} } = location;
        let questionsForDisplay = [];
        const params = parseQueryString();
        if (params.workflow_id) { appStorage.setItem('workflow_id', params.workflow_id); }
        const workflowResponse = await getWorkflow(language);
        const { worflow_template: questionsFromWorkflow, workflow_id } = workflowResponse;
        questionsForDisplay = questionsFromWorkflow;

        const currentQuestion = questionById(currentQuestionId, questionsForDisplay);
        await setState(state => ({
          ...state,
          isLoading: false,
          allQuestions: questionsFromWorkflow,
          questions: questionsForDisplay,
          workflow_id,
          currentQuestion,
          renderedQuestions: [currentQuestion],
          workflowResponse
        }));

        if (!currentQuestionId) {
          const firstQuestionId = questionId(questionsForDisplay[0]);
          history.replace(`/general-workflow/${firstQuestionId}`, routeState);
        }
      } catch (e) {
        logError(e);
        setState(state => ({
          ...state, isLoading: false, error: true
        }));
      }
      hideLoading();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const onAnswerSelected = question => selectedAnswer => async () => {
    if (selectedAnswer.id) {
      updateInStorage(question.question_id, selectedAnswer.id);
    } else if (Array.isArray(selectedAnswer)) {
      updateInStorage(question.question_id, selectedAnswer);
    }
    if (selectedAnswer.current ? selectedAnswer.current.length === 0 : Object.entries(selectedAnswer).length === 0) {
      alert(t('QUESTION_ANSWERING_ADVISE'));
      return;
    }

    const nextQuestion = nextQuestionId(questionId(question), state.questions);
    const stateString = setAnswer(questionId(question), formatAnswers(selectedAnswer), parseQueryString().s);
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set('s', stateString);

    if (nextQuestion) {
      provideNextQuestion(nextQuestion, urlParams);
    } else {
      if (state.workflowResponse.survey_result_parameter && state.workflowResponse.survey_result_parameter.type === 'custom-message')
        raiseEventAndDisplayMessage();
      else showResults(urlParams);
    }
  };

  const provideNextQuestion = (nextQuestion, urlParams) => {
    if (hasChatBotQuestionnaire()) {
      const questionToAppend = questionById(nextQuestion, state.questions);
      setState(state => ({ ...state, currentQuestion: questionToAppend, renderedQuestions: [...state.renderedQuestions, questionToAppend] }));
    }
    else history.push(`/general-workflow/${nextQuestion}?${urlParams.toString()}`);
  }

  const raiseEventAndDisplayMessage = async () => {
    //raise event only when workflow has survey_event_parameter
    if (state.workflowResponse && state.workflowResponse.survey_event_parameter) {
      const userId = await getUserId();
      let workflowResponse = {
        source_user_id: userId,
        workflow_id: state.workflow_id,
        language: languageForApi(language),
        workflow_responses: responseForPosting(state.workflowResponse.worflow_template)
      };
      const eventUrl = state.workflowResponse.survey_event_parameter;
      await fireSurveyResultEventAtUrl(language, {}, workflowResponse, eventUrl);
    }

    setState(state => ({
      ...state,
      actionName: 'DISPLAY_MESSAGE',
    }));
  }

  const showResults = async (urlParams) => {
    if (hasChatBotQuestionnaire()) {
      let questionToAppend = { loading: true };
      setState(state => ({ ...state, renderedQuestions: [...state.renderedQuestions, questionToAppend] }));
      const results = await getComputeRisk(language);
      const newRenderedQuestions = state.renderedQuestions.filter(question => !question.loading);
      newRenderedQuestions.push(results);
      setState(state => ({ ...state, currentQuestion: null, renderedQuestions: newRenderedQuestions }));
    }
    else {
      appStorage.removeItem('getComputedRisk');
      appStorage.removeItem('fireSurveyResultEvent');
      history.replace('/');
      history.push(`/general-workflow/results?${urlParams.toString()}`);
    }
  }

  if (state.isLoading) return <div />;

  if (state.error) return <Error handleRetry={() => history.push('/')} />;
  return (
    <>
      <div className={styles.container}>
        <div>{state.actionName === 'NONE' ? loadQuestionView(t, currentQuestionId, state, onAnswerSelected, hasChatBotQuestionnaire)
          : renderMessageView(t, state.workflowResponse, history)}</div>
      </div>
    </>
  );
};

function renderMessageView(t, workflowResponse, history) {
  let message;
  if (workflowResponse && workflowResponse.survey_result_parameter)
    message = workflowResponse.survey_result_parameter.value || t('NO_CHANGE_UNABLE_TO_CLOSE');
  return (
    <div>
      <div style={{
        display: 'flex', flexFlow: 'column', alignSelf: 'center'
      }}
      >
        {message}
      </div>
      <Button
        style={{
          marginTop: '30px', width: '70px', height: '35px', alignSelf: 'center'
        }}
        onClick={() => {
          appStorage.resetSurveyData();
          history.push('/')
          //deleteQuestionIdsFromSessionStorage(workflowResponse.worflow_template);
        }}
        variant="contained"
        color="primary"
      >
        {t('DONE')}
      </Button>
    </div>
  );
}
export const renderQuestionCard = (t, currentQuestionId, state, onAnswerSelected, hasChatBotQuestionnaire) => {
  if (hasChatBotQuestionnaire() && state.renderedQuestions.length > 0) {
    return <ChatBot question={state.currentQuestion} questions={state.renderedQuestions} onAnswerSelected={onAnswerSelected(state.currentQuestion)} isNoChange={false}
    />
  }
  if (!hasChatBotQuestionnaire() && currentQuestionId) {
    return <QuestionCard question={questionById(currentQuestionId, state.questions)} enabled={!state.isLoading} onAnswerSelected={onAnswerSelected(questionById(currentQuestionId, state.questions))} />
  }
  return null;
}

function loadQuestionView(t, currentQuestionId, state, onAnswerSelected, hasChatBotQuestionnaire) {
  const displayType = hasChatBotQuestionnaire() ? 'chatbot' : 'questionnaire';
  return (
    <div style={isDesktopScreen() ? inlineStyles.desktopContainer[displayType] : inlineStyles.mobileContainer[displayType]}>
      <div className={styles.title}>{t('SYMPTOM_CHECKER_TITLE')}</div>
      <div className={styles.subtitle}>
        {t('QUESTION_ANSWERING_ADVISE')}
      </div>
      {renderQuestionCard(t, currentQuestionId, state, onAnswerSelected, hasChatBotQuestionnaire)}
    </div>
  );
}